
"use client"

import { motion } from "framer-motion"
import Link from "next/link"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Calendar, User, ArrowLeft, Share2, Printer } from "lucide-react"
import TextReveal from "@/components/animated/text-reveal"
import GradientBackground from "@/components/animated/gradient-background"
import Breadcrumbs from "@/components/seo/breadcrumbs"

interface Resource {
    title: string
    slug: string
    date: string
    category: string
    excerpt: string
    content: string
}

export default function ResourceDetailClient({ resource }: { resource: Resource }) {
    return (
        <div className="min-h-screen relative pb-20">
            <GradientBackground />

            <div className="container mx-auto px-4 py-8 relative z-10">
                <Breadcrumbs items={[
                    { name: "Resources", url: "/resources" },
                    { name: resource.title, url: `/resources/${resource.slug}` }
                ]} />

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                    className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 md:p-12 mb-12 border border-slate-100"
                >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                        <Button variant="ghost" size="sm" asChild className="w-fit text-slate-500 hover:text-slate-900 px-0">
                            <Link href="/resources">
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back to Resources
                            </Link>
                        </Button>

                        <div className="flex items-center space-x-3 text-slate-500 text-sm">
                            <div className="flex items-center">
                                <Calendar className="h-4 w-4 mr-1" />
                                {new Date(resource.date).toLocaleDateString()}
                            </div>
                            <span>•</span>
                            <div className="flex items-center">
                                <User className="h-4 w-4 mr-1" />
                                RMM Technical Team
                            </div>
                        </div>
                    </div>

                    <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 mb-6 px-4 py-1 text-sm">{resource.category}</Badge>

                    <TextReveal>
                        <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-8 leading-tight">
                            {resource.title}
                        </h1>
                    </TextReveal>

                    <p className="text-xl text-slate-600 mb-12 leading-relaxed border-l-4 border-blue-500 pl-6 italic">
                        {resource.excerpt}
                    </p>

                    <div className="prose prose-slate prose-lg max-w-none prose-headings:font-display prose-headings:font-bold prose-a:text-blue-600 prose-img:rounded-3xl prose-img:shadow-2xl">
                        {/* Realistically, this would be MDX or sanitized HTML */}
                        <div className="space-y-8 text-slate-700 leading-relaxed">
                            <p>
                                Industrial hardware plays a critical role in the safety and efficiency of any large-scale project. Whether you're working in construction, manufacturing, or infrastructure, selecting the right components is the foundation of success.
                            </p>

                            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Key Considerations</h2>
                            <p>
                                When selecting hardware for industrial applications in Sri Lanka, environmental factors such as humidity and coastal proximity must be considered. Corrosion resistance is often the primary concern for long-term durability.
                            </p>

                            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100 my-10">
                                <h3 className="text-xl font-bold text-slate-900 mb-4">Expert Tip:</h3>
                                <p className="text-slate-600 m-0">
                                    Always verify the material certification for structural components. At Real Metro Metal, we ensure all our products meet international standards for strength and composition.
                                </p>
                            </div>

                            <h2 className="text-3xl font-bold text-slate-900 mt-12 mb-6">Installation Standards</h2>
                            <p>
                                Correct installation is as important as the quality of the hardware itself. Improper fitting can lead to premature failure, safety hazards, and increased maintenance costs.
                            </p>

                            <p className="mt-10">
                                Contact our technical team today for specific inquiries or load-bearing calculations for your next project.
                            </p>
                        </div>
                    </div>

                    <div className="mt-16 pt-8 border-t border-slate-100 flex flex-wrap gap-4 items-center justify-between">
                        <div className="flex space-x-2">
                            <Button variant="outline" size="sm" className="rounded-full">
                                <Share2 className="h-4 w-4 mr-2" /> Share
                            </Button>
                            <Button variant="outline" size="sm" className="rounded-full">
                                <Printer className="h-4 w-4 mr-2" /> Print PDF
                            </Button>
                        </div>

                        <Button size="lg" className="bg-blue-600 hover:bg-blue-700 shadow-xl" asChild>
                            <Link href="/contact">Inquire About This Category</Link>
                        </Button>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}
